import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ComboboxOption } from '@/components/Combobox';

interface MultiComboboxProps {
  values: ComboboxOption[];
  onChange: (values: ComboboxOption[]) => void;
  options: ComboboxOption[];
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
}

const MAX_VISIBLE = 50;
const PANEL_MAX_HEIGHT = 240;

/**
 * Multi-value filter input: chips for selected values, a suggestions dropdown
 * (portal-rendered, never clipped) with already-selected options hidden,
 * Enter commits free text, Backspace on empty input removes the last chip.
 */
export function MultiCombobox({
  values,
  onChange,
  options,
  placeholder,
  loading = false,
  disabled = false,
  className,
}: MultiComboboxProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState<{ left: number; top: number; width: number; up: boolean } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const selected = new Set(values.map((v) => v.value));
    const base = options.filter((o) => !selected.has(o.value));
    const q = text.trim().toLowerCase();
    if (!q) return base.slice(0, MAX_VISIBLE);
    return base
      .filter((o) => {
        const label = String(o.label ?? '');
        const val = String(o.value ?? '');
        return label.toLowerCase().includes(q) || val.toLowerCase().includes(q);
      })
      .slice(0, MAX_VISIBLE);
  }, [options, values, text]);

  useEffect(() => setHighlight(0), [text, values]);

  const openList = () => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (rect) {
      const up = rect.bottom + PANEL_MAX_HEIGHT + 8 > window.innerHeight && rect.top > PANEL_MAX_HEIGHT + 8;
      setPos({ left: rect.left, width: rect.width, top: up ? rect.top : rect.bottom, up });
    }
    setOpen(true);
  };

  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (boxRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      close();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const add = (opt: ComboboxOption) => {
    if (values.some((v) => v.value === opt.value)) return;
    onChange([...values, opt]);
    setText('');
    inputRef.current?.focus();
  };

  const removeAt = (i: number) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <div
      ref={boxRef}
      onClick={() => {
        if (!disabled) {
          inputRef.current?.focus();
          if (!open) openList();
        }
      }}
      className={cn(
        'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-sm shadow-card transition-colors cursor-text',
        'focus-within:ring-2 focus-within:ring-ring/30 focus-within:border-ring',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      {values.map((v, i) => (
        <span
          key={`${v.value}-${i}`}
          className="inline-flex items-center gap-1 rounded-md bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-secondary-foreground animate-scale-in"
        >
          <span className="max-w-40 truncate">{v.label}</span>
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              removeAt(i);
            }}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={`Remove ${v.label}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={text}
        disabled={disabled}
        placeholder={values.length === 0 ? placeholder : ''}
        onChange={(e) => {
          setText(e.target.value);
          if (!open) openList();
        }}
        onFocus={openList}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) openList();
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (open && filtered[highlight]) {
              add(filtered[highlight]);
            } else if (text.trim()) {
              add({ label: text.trim(), value: text.trim() });
            }
          } else if (e.key === 'Backspace' && !text && values.length > 0) {
            removeAt(values.length - 1);
          }
        }}
        className="min-w-20 flex-1 bg-transparent py-0.5 text-xs outline-none placeholder:text-muted-foreground"
      />
      <span className="ml-auto pl-1 text-muted-foreground">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-4 w-4" />}
      </span>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            style={
              pos.up
                ? { left: pos.left, width: pos.width, bottom: window.innerHeight - pos.top + 4 }
                : { left: pos.left, width: pos.width, top: pos.top + 4 }
            }
            className="fixed z-[100] max-h-60 overflow-auto rounded-md border bg-popover p-1 shadow-pop animate-scale-in"
          >
            {loading && options.length === 0 ? (
              <div className="px-2 py-3 text-center text-xs text-muted-foreground">Loading options…</div>
            ) : filtered.length === 0 ? (
              <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                {options.length === 0
                  ? 'No predefined options — type a value and press Enter'
                  : text
                    ? 'No matches — press Enter to add the typed value'
                    : 'All options selected'}
              </div>
            ) : (
              filtered.map((o, i) => (
                <button
                  key={`${o.value}-${i}`}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(o);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={cn(
                    'flex w-full items-center rounded-sm px-2 py-1.5 text-left text-xs transition-colors',
                    i === highlight ? 'bg-accent text-accent-foreground' : 'text-foreground'
                  )}
                >
                  <span className="truncate">{o.label}</span>
                  {o.label !== o.value && (
                    <span className="ml-2 shrink-0 text-[10px] text-muted-foreground truncate">{o.value}</span>
                  )}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
